import { ProductCodeSelector } from "@/shared/selectors/ProductCodeSelector";
import { ShiftSelector } from "@/shared/selectors/ShiftSelector";
import {
	Checkbox,
	FormControl,
	Grid2,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	type SelectChangeEvent,
} from "@mui/material";
import { Moment } from "moment";
import { FC, useEffect, useState } from "react";
import {
	EndFilter,
	StartFilter,
} from "../../../../delays/shared/components/StartEndFilter";

export type BarStatusFilter = {
	good: boolean;
	tooLong: boolean;
	tooShort: boolean;
};

type Props = {
	onFilterChange: (startDate: Moment, endDate: Moment) => void;
	initialStartDate: Moment;
	initialEndDate: Moment;
	productCode: number | null;
	onProductCodeChange: (value: number | null) => void;
	barStatusFilter: BarStatusFilter;
	onBarStatusFilterChange: (filter: BarStatusFilter) => void;
	showShiftFilter: boolean;
	shiftId?: number | null;
	onShiftIdChange?: (value: number | null) => void;
};

const STATUS_OPTIONS = [
	{ key: "good" as const, label: "Good" },
	{ key: "tooLong" as const, label: "Too Long" },
	{ key: "tooShort" as const, label: "Too Short" },
];

const BarStatusSelect: FC<{
	value: BarStatusFilter;
	onChange: (filter: BarStatusFilter) => void;
}> = ({ value, onChange }) => {
	const selected = STATUS_OPTIONS.filter((o) => value[o.key]).map((o) => o.key);

	const handleChange = (event: SelectChangeEvent<string[]>) => {
		const val = event.target.value as string[];
		onChange({
			good: val.includes("good"),
			tooLong: val.includes("tooLong"),
			tooShort: val.includes("tooShort"),
		});
	};

	const renderValue = (sel: string[]) => {
		if (sel.length === STATUS_OPTIONS.length) return "All";
		if (sel.length === 0) return "None";
		return STATUS_OPTIONS.filter((o) => sel.includes(o.key))
			.map((o) => o.label)
			.join(", ");
	};

	return (
		<FormControl size="small" fullWidth>
			<InputLabel>Bar Status</InputLabel>
			<Select
				multiple
				value={selected}
				onChange={handleChange}
				renderValue={renderValue}
				label="Bar Status"
			>
				{STATUS_OPTIONS.map((option) => (
					<MenuItem key={option.key} value={option.key} dense sx={{ py: 0.25 }}>
						<ListItemText primary={option.label} />
						<Checkbox
							checked={value[option.key]}
							size="small"
							sx={{ p: 0.5, ml: 0.5 }}
						/>
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};

export const Filters: FC<Props> = ({
	onFilterChange,
	initialStartDate,
	initialEndDate,
	productCode,
	onProductCodeChange,
	barStatusFilter,
	onBarStatusFilterChange,
	showShiftFilter,
	shiftId,
	onShiftIdChange,
}) => {
	const [startDate, setStartDate] = useState<Moment>(initialStartDate);
	const [endDate, setEndDate] = useState<Moment>(initialEndDate);

	useEffect(() => {
		setStartDate(initialStartDate);
		setEndDate(initialEndDate);
	}, [initialStartDate, initialEndDate]);

	const handleStartDateChange = (date: Moment | null) => {
		if (date) {
			setStartDate(date);
			onFilterChange(date, endDate);
		}
	};

	const handleEndDateChange = (date: Moment | null) => {
		if (date) {
			setEndDate(date);
			onFilterChange(startDate, date);
		}
	};

	return (
		<Grid2 container style={{ alignItems: "center" }} spacing={2}>
			<Grid2 size={{ xs: 12, md: 3 }}>
				<StartFilter value={startDate} onChange={handleStartDateChange} />
			</Grid2>
			<Grid2 size={{ xs: 12, md: 3 }}>
				<EndFilter value={endDate} onChange={handleEndDateChange} />
			</Grid2>
			<Grid2 size={{ xs: 12, md: 3 }}>
				<ProductCodeSelector
					label="Product Code"
					value={productCode}
					onChange={onProductCodeChange}
				/>
			</Grid2>
			{showShiftFilter && onShiftIdChange && (
				<Grid2 size={{ xs: 12, md: 3 }}>
					<ShiftSelector
						label="Shift"
						value={shiftId ?? null}
						onChange={onShiftIdChange}
						productionDate={startDate.toDate()}
					/>
				</Grid2>
			)}
			<Grid2 size={{ xs: 12, md: 3 }}>
				<BarStatusSelect
					value={barStatusFilter}
					onChange={onBarStatusFilterChange}
				/>
			</Grid2>
		</Grid2>
	);
};
